import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { InsightAnswerBody } from "../../admin/assistantUi";
import { isDashboardPath, ROUTES } from "../../admin/routes";
import NexusFlowQuestionPanel, {
  filterFlowActions,
  inferCompanyActionsFromText,
  stripRedundantClarificationText,
} from "./NexusFlowQuestionPanel";

function SectionBlock({ title, items, variant = "default", landing = false }) {
  if (!items?.length) return null;
  const styles = {
    default: landing
      ? "border-slate-200/90 bg-slate-50/90"
      : "border-slate-100 bg-slate-50/80",
    finding: landing
      ? "border-[#004f73]/15 bg-[#004f73]/[0.04]"
      : "border-[#004f73]/10 bg-[#004f73]/[0.03]",
    risk: landing
      ? "border-amber-200/90 bg-amber-50/90"
      : "border-amber-100 bg-amber-50/80",
  };
  const labelStyles = {
    default: landing ? "text-slate-600" : "text-slate-600",
    finding: landing ? "text-[#004f73]" : "text-[#004f73]",
    risk: landing ? "text-amber-800" : "text-amber-800",
  };

  return (
    <div className={`rounded-xl border px-3 py-2.5 ${styles[variant] || styles.default}`}>
      <p className={`text-[10px] font-semibold uppercase tracking-wider mb-2 ${labelStyles[variant] || labelStyles.default}`}>
        {title}
      </p>
      <ul className="space-y-1.5">
        {items.map((item, i) => {
          const text = typeof item === "string" ? item : item?.text || item?.action || "";
          const reason = typeof item === "object" ? item?.reason : "";
          if (!text) return null;
          return (
            <li
              key={i}
              className={`text-sm leading-snug flex gap-2 ${landing ? "text-slate-800" : "text-slate-800"}`}
            >
              <span className={`shrink-0 mt-1.5 h-1.5 w-1.5 rounded-full ${landing ? "bg-[#004f73]/50" : "bg-[#004f73]/50"}`} aria-hidden />
              <span>
                {text}
                {reason ? (
                  <span className="text-slate-500"> — {reason}</span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function StructuredSections({ sections, landing = false }) {
  if (!sections?.length) return null;
  return (
    <div className="space-y-2.5">
      {sections.map((section, idx) => {
        const items = section.items || [];
        if (!items.length) return null;
        if (section.kind === "findings") {
          return (
            <SectionBlock
              key={`sec-${idx}`}
              title="Key points"
              items={items}
              variant="finding"
              landing={landing}
            />
          );
        }
        if (section.kind === "risks") {
          return (
            <SectionBlock key={`sec-${idx}`} title="Risks" items={items} variant="risk" landing={landing} />
          );
        }
        return null;
      })}
    </div>
  );
}

function KpiRow({ metrics }) {
  if (!metrics?.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {metrics.map((m) => (
        <div
          key={m.label}
          className="rounded-xl border border-[#004f73]/15 bg-gradient-to-br from-[#004f73]/[0.04] to-white px-3 py-2 min-w-[7rem]"
        >
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{m.label}</p>
          <p className="text-lg font-semibold text-[#004f73] tabular-nums">{m.value}</p>
        </div>
      ))}
    </div>
  );
}

function SelectableRecipientTable({ artifact, onConfirmReminders, busy = false }) {
  const selectKey = artifact?.selectKey || "invite_id";
  const rows = artifact?.rows || [];
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    const next = new Set();
    if (artifact?.defaultSelected !== false) {
      rows.forEach((row) => {
        const id = String(row[selectKey] || "").trim();
        if (id) next.add(id);
      });
    }
    setSelected(next);
  }, [artifact, rows, selectKey]);

  const allChecked = rows.length > 0 && rows.every((row) => selected.has(String(row[selectKey] || "")));
  const someChecked = rows.some((row) => selected.has(String(row[selectKey] || "")));

  const toggleAll = () => {
    if (allChecked) {
      setSelected(new Set());
      return;
    }
    const next = new Set();
    rows.forEach((row) => {
      const id = String(row[selectKey] || "").trim();
      if (id) next.add(id);
    });
    setSelected(next);
  };

  const toggleOne = (id) => {
    const key = String(id || "").trim();
    if (!key) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const displayRows = useMemo(
    () =>
      rows.map((row) => ({
        id: String(row[selectKey] || ""),
        participant: row.participant || row.Participant || "—",
        email: row.email || row.Email || "—",
        company: row.company || row.Company || "—",
        status: row.status || row.Status || "—",
      })),
    [rows, selectKey]
  );

  if (!displayRows.length) return null;

  const confirmSend = () => {
    const ids = displayRows.map((r) => r.id).filter((id) => selected.has(id));
    if (!ids.length || typeof onConfirmReminders !== "function") return;
    onConfirmReminders({
      invite_ids: ids,
      flow_id: artifact?.flow_id || null,
    });
  };

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
      {artifact?.title ? (
        <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-700">
          {artifact.title}
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50/80">
              <th className="w-10 px-2 py-2">
                <input
                  type="checkbox"
                  className="rounded border-slate-300"
                  checked={allChecked}
                  ref={(el) => {
                    if (el) el.indeterminate = !allChecked && someChecked;
                  }}
                  onChange={toggleAll}
                  aria-label="Select all recipients"
                />
              </th>
              <th className="text-left px-2 py-2 font-semibold text-slate-600">Participant</th>
              <th className="text-left px-2 py-2 font-semibold text-slate-600">Email</th>
              <th className="text-left px-2 py-2 font-semibold text-slate-600">Company</th>
              <th className="text-left px-2 py-2 font-semibold text-slate-600">Status</th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row) => (
              <tr key={row.id} className="border-t border-slate-100">
                <td className="px-2 py-2 align-middle">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300"
                    checked={selected.has(row.id)}
                    onChange={() => toggleOne(row.id)}
                    aria-label={`Select ${row.participant}`}
                  />
                </td>
                <td className="px-2 py-2 text-slate-800">{row.participant}</td>
                <td className="px-2 py-2 text-slate-800">{row.email}</td>
                <td className="px-2 py-2 text-slate-800">{row.company || "—"}</td>
                <td className="px-2 py-2 text-slate-700">{row.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-3 py-2.5 border-t border-slate-100 flex items-center justify-between gap-3 bg-slate-50/50">
        <span className="text-[11px] text-slate-500">
          {selected.size} of {displayRows.length} selected
        </span>
        <button
          type="button"
          disabled={!someChecked || busy}
          onClick={confirmSend}
          className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {artifact?.confirmLabel || "Confirm send"}
        </button>
      </div>
    </div>
  );
}

function DataTable({ title, columns, rows }) {
  if (!rows?.length) return null;
  const cols = columns || Object.keys(rows[0] || {});
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden">
      {title ? (
        <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-700">
          {title}
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50/80">
              {cols.map((c) => (
                <th key={c} className="text-left px-3 py-2 font-semibold text-slate-600">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-t border-slate-100">
                {cols.map((c) => (
                  <td key={c} className="px-3 py-2 text-slate-800">
                    {row[c] ?? "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PillarBarChart({ title, bars, highlightKey }) {
  if (!bars?.length) return null;
  const maxVal = Math.max(...bars.map((b) => Number(b.value) || 0), 1);

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
      {title ? (
        <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-700">
          {title}
        </div>
      ) : null}
      <div className="p-3 space-y-2">
        {bars.map((bar) => {
          const pct = Math.round(((Number(bar.value) || 0) / maxVal) * 100);
          const highlighted = bar.highlight || bar.key === highlightKey;
          return (
            <div key={bar.key || bar.label} className="space-y-1">
              <div className="flex items-center justify-between gap-2 text-xs">
                <span
                  className={`font-medium truncate ${highlighted ? "text-[#004f73]" : "text-slate-700"}`}
                >
                  {bar.label}
                  {highlighted ? (
                    <span className="ml-1.5 text-[10px] font-semibold uppercase text-amber-600">
                      focus
                    </span>
                  ) : null}
                </span>
                <span className="text-slate-500 shrink-0 tabular-nums">{bar.value}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    highlighted
                      ? "bg-gradient-to-r from-amber-400 to-orange-500"
                      : "bg-gradient-to-r from-[#004f73] to-cyan-500"
                  }`}
                  style={{ width: `${Math.max(pct, 4)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FunnelChart({ title, steps, highlightStep }) {
  if (!steps?.length) return null;
  const maxReached = Math.max(...steps.map((s) => s.reached || 0), 1);

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
      {title ? (
        <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-700">
          {title}
        </div>
      ) : null}
      <div className="p-3 space-y-2">
        {steps.map((step, idx) => {
          const pct = maxReached ? Math.round(((step.reached || 0) / maxReached) * 100) : 0;
          const dropPct = step.dropOffPct ?? 0;
          const highlighted =
            step.highlight || (highlightStep && step.title === highlightStep);
          return (
            <div key={step.title || idx} className="space-y-1">
              <div className="flex items-center justify-between gap-2 text-xs">
                <span
                  className={`font-medium truncate ${highlighted ? "text-[#004f73]" : "text-slate-700"}`}
                >
                  {step.title}
                  {highlighted ? (
                    <span className="ml-1.5 text-[10px] font-semibold uppercase text-amber-600">
                      worst drop-off
                    </span>
                  ) : null}
                </span>
                <span className="text-slate-500 shrink-0 tabular-nums">
                  {step.reached} reached · {dropPct}% drop
                </span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    highlighted
                      ? "bg-gradient-to-r from-amber-400 to-orange-500"
                      : "bg-gradient-to-r from-[#004f73] to-cyan-500"
                  }`}
                  style={{ width: `${Math.max(pct, 4)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CollapsibleSources({ citations, dataSources }) {
  const items = [
    ...(citations || []).map((c) => ({ key: c.doc_id || c.title, ...c, kind: "citation" })),
  ];
  const srcTags = (dataSources || []).filter(Boolean);
  const total = items.length + srcTags.length;
  const [open, setOpen] = useState(false);

  if (!total) return null;

  return (
    <div className="rounded-xl border border-slate-200/90 bg-slate-50/50 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors"
        aria-expanded={open}
      >
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
          Sources
          <span className="ml-1.5 font-normal text-slate-400">({total})</span>
        </span>
        <svg
          className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open ? (
        <div className="px-3 pb-3 pt-0 space-y-2 border-t border-slate-100">
          {items.map((c) => (
            <div key={c.key} className="text-xs text-slate-600 border-l-2 border-[#004f73]/25 pl-2">
              <span className="font-medium text-slate-800">{c.title}</span>
              {c.category && !String(c.title || "").includes("Studio") ? (
                <span className="text-slate-400"> · {c.category}</span>
              ) : null}
              {c.excerpt ? <p className="text-slate-500 mt-0.5 leading-relaxed">{c.excerpt}</p> : null}
            </div>
          ))}
          {srcTags.length ? (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {srcTags.map((src) => (
                <span
                  key={src}
                  className="text-[10px] font-medium uppercase tracking-wide text-slate-500 bg-white border border-slate-200 rounded px-2 py-0.5"
                >
                  {src}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function NexusStructuredReply({
  structured,
  fallbackText = "",
  relatedPages = [],
  dataSources = [],
  onNavigate,
  onClarify,
  onConfirmReminders,
  reminderSendBusy = false,
  currentPath = "",
  variant = "default",
}) {
  const navigate = useNavigate();
  const landing = variant === "landing";
  const s = structured && typeof structured === "object" ? structured : null;
  const summary = s?.summary || fallbackText;
  const sections = s?.sections || [];
  const artifacts = s?.artifacts || [];
  const rawActions = (s?.actions || []).filter((a) => a?.type !== "navigate");
  const citations = s?.citations || [];
  const rawPages = s?.related_pages?.length ? s.related_pages : relatedPages;
  const validation = s?.validation;
  const confidence = s?.confidence;

  const onDashboard =
    isDashboardPath(currentPath) ||
    (currentPath === ROUTES.dashboard && landing);

  const pages = (() => {
    const seen = new Set();
    const out = [];
    for (const page of rawPages || []) {
      const path = (page?.path || "").trim();
      if (!path || seen.has(path)) continue;
      if (onDashboard && isDashboardPath(path)) continue;
      seen.add(path);
      out.push(page);
    }
    return out;
  })();

  const actions = rawActions.filter((a) => {
    if (a?.type === "navigate") return false;
    const label = (a?.label || "").trim().toLowerCase();
    if (label.startsWith("open ") && pages.some((p) => label.includes((p.label || "").toLowerCase()))) {
      return false;
    }
    if (a?.type === "clarify") {
      const text = (a?.label || "").trim();
      if (text.length > 72 && text.includes("?")) return false;
    }
    return true;
  });

  let flowActions = filterFlowActions(actions);
  if (!flowActions.length && summary) {
    const inferred = inferCompanyActionsFromText(summary);
    if (inferred.length) flowActions = inferred;
  }
  const hasFlowActions = flowActions.length > 0;
  const displaySummary = hasFlowActions
    ? stripRedundantClarificationText(summary, actions)
    : summary;

  const go = (path, state) => {
    if (!path) return;
    if (typeof onNavigate === "function") {
      onNavigate(path, state);
      return;
    }
    navigate(path, state ? { state } : undefined);
  };

  if (!displaySummary && !artifacts.length && !sections.length && !hasFlowActions && !pages.length) {
    if (fallbackText?.trim()) {
      return (
        <div className="text-sm leading-relaxed text-slate-800">
          <InsightAnswerBody text={fallbackText} />
        </div>
      );
    }
    return null;
  }

  return (
    <div className="space-y-4">
      {confidence ? (
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`text-[10px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 ${
              confidence === "high"
                ? "bg-emerald-50 text-emerald-700"
                : confidence === "medium"
                  ? "bg-amber-50 text-amber-700"
                  : "bg-slate-100 text-slate-600"
            }`}
          >
            {validation?.passed === false ? "Review" : confidence} confidence
          </span>
          {validation?.checks?.[0] ? (
            <span className="text-[10px] truncate text-slate-400">
              {validation.checks[0]}
            </span>
          ) : null}
          {s?.answer_kind ? (
            <span className="text-[10px] truncate text-slate-400 hidden sm:inline">
              · {String(s.answer_kind).replace(/_/g, " ")}
            </span>
          ) : null}
        </div>
      ) : null}

      {hasFlowActions ? (
        <NexusFlowQuestionPanel
          actions={flowActions}
          onClarify={onClarify}
          landing={landing}
          answerKind={s?.answer_kind || ""}
        />
      ) : null}

      {displaySummary ? (
        <div className="text-sm leading-relaxed text-slate-800">
          <InsightAnswerBody text={displaySummary} />
        </div>
      ) : null}

      {!hasFlowActions ? <StructuredSections sections={sections} landing={landing} /> : null}

      {artifacts.map((artifact, idx) => {
        if (artifact.type === "kpi_row") {
          return <KpiRow key={`kpi-${idx}`} metrics={artifact.metrics} />;
        }
        if (artifact.type === "funnel_chart") {
          return (
            <FunnelChart
              key={`funnel-${idx}`}
              title={artifact.title}
              steps={artifact.steps}
              highlightStep={artifact.highlightStep}
            />
          );
        }
        if (artifact.type === "pillar_chart") {
          return (
            <PillarBarChart
              key={`pillar-${idx}`}
              title={artifact.title}
              bars={artifact.bars}
              highlightKey={artifact.highlightKey}
            />
          );
        }
        if (artifact.type === "table") {
          return (
            <DataTable
              key={`table-${idx}`}
              title={artifact.title}
              columns={artifact.columns}
              rows={artifact.rows}
            />
          );
        }
        if (artifact.type === "selectable_table") {
          return (
            <SelectableRecipientTable
              key={`sel-${idx}`}
              artifact={artifact}
              onConfirmReminders={onConfirmReminders}
              busy={reminderSendBusy}
            />
          );
        }
        return null;
      })}

      <CollapsibleSources citations={citations} dataSources={dataSources} />

      {pages?.length ? (
        <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
          {pages.map((page) => (
            <button
              key={page.path}
              type="button"
              onClick={() => go(page.path)}
              className="text-xs font-medium text-[#004f73] bg-[#004f73]/[0.06] border border-[#004f73]/20 rounded-full px-3 py-1 hover:bg-[#004f73]/10 transition-colors"
              title={page.reason || page.label}
            >
              {page.label} →
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
